import { useEffect, useState } from 'react';
import { Printer, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { trpc } from '@/lib/trpc';
import { ETIQUETAS_POR_FOLHA, gerarEtiquetasPdf } from '@/lib/etiquetas/gerarEtiquetasPdf';

export interface ProdutoSelecionado {
  id: string;
  codigo: string;
  nome: string;
  preco: string;
  quantidadeEstoque: number;
  /** Usado pela categorização em lote para avisar o que será sobrescrito. */
  categoria: string | null;
}

interface EtiquetasModalProps {
  isOpen: boolean;
  onClose: () => void;
  produtos: ProdutoSelecionado[];
}

export function EtiquetasModal({ isOpen, onClose, produtos }: EtiquetasModalProps) {
  const [posicaoInicial, setPosicaoInicial] = useState(1);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const produtoIds = produtos.map((p) => p.id);

  /**
   * Só as peças ainda na arara.
   *
   * Reimprimir um produto com 2 vendidas gera 3 etiquetas, não 5 — evita colar
   * numa peça nova a etiqueta de uma peça que já saiu.
   */
  const { data: unidades, isLoading } = trpc.produtos.listUnidadesDisponiveis.useQuery(
    { produtoIds },
    { enabled: isOpen && produtoIds.length > 0, staleTime: 0 }
  );

  useEffect(() => {
    if (!isOpen) return;
    setPosicaoInicial(1);
    setErro(null);
  }, [isOpen]);

  const porProduto = new Map(produtos.map((p) => [p.id, p]));

  const etiquetas = (unidades ?? []).flatMap((unidade) => {
    const produto = porProduto.get(unidade.produtoId);
    if (!produto) return [];
    return [{ codigo: unidade.codigo, nome: produto.nome, preco: produto.preco }];
  });

  const total = etiquetas.length;
  const folhas = total > 0 ? Math.ceil((posicaoInicial - 1 + total) / ETIQUETAS_POR_FOLHA) : 0;

  const contagemPorProduto = new Map<string, number>();
  for (const unidade of unidades ?? []) {
    contagemPorProduto.set(unidade.produtoId, (contagemPorProduto.get(unidade.produtoId) ?? 0) + 1);
  }

  const handleGerar = async () => {
    setErro(null);
    setGerando(true);

    try {
      await gerarEtiquetasPdf(etiquetas, posicaoInicial);
      onClose();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível gerar as etiquetas');
    } finally {
      setGerando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Imprimir etiquetas">
      <div className="space-y-4">
        {produtos.length === 0 ? (
          <p className="py-6 text-center text-gray-500">
            Selecione ao menos um produto na lista para imprimir etiquetas.
          </p>
        ) : (
          <>
            <div className="max-h-64 space-y-2 overflow-auto">
              {produtos.map((produto) => (
                <div
                  key={produto.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900">{produto.nome}</p>
                    <p className="font-mono text-xs text-gray-500">{produto.codigo}</p>
                  </div>
                  <span className="whitespace-nowrap text-sm text-gray-600">
                    {isLoading ? '...' : `${contagemPorProduto.get(produto.id) ?? 0} peças`}
                  </span>
                </div>
              ))}
            </div>

            <Input
              label={`Começar na posição da folha (1 a ${ETIQUETAS_POR_FOLHA})`}
              type="number"
              min={1}
              max={ETIQUETAS_POR_FOLHA}
              value={posicaoInicial}
              onChange={(e) => setPosicaoInicial(Number.parseInt(e.target.value, 10) || 1)}
            />
            <p className="-mt-2 text-xs text-gray-500">
              Deixa em branco as posições já usadas, para reaproveitar uma folha começada.
            </p>

            <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
              {total} {total === 1 ? 'etiqueta' : 'etiquetas'} · uma por peça ·{' '}
              {folhas === 1 ? '1 folha' : `${folhas} folhas`} A4
            </div>

            {erro && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{erro}</span>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleGerar} isLoading={gerando} disabled={total === 0 || isLoading}>
            <Printer className="mr-2 h-4 w-4" />
            Gerar PDF
          </Button>
        </div>
      </div>
    </Modal>
  );
}
